import { useEffect, useState } from 'react';

export interface TokenSpinProps {
  /** Render size in px. Intrinsic asset is 256×256 so we downscale only. */
  size?: number;
  /** Extra class passed through to the underlying <img>. */
  className?: string;
  /** Accessible label / alt text. */
  title?: string;
}

/**
 * Spinning EVI reward token. Rendered as an animated WebP so we get
 * native looping, transparent background, and ~190KB over the wire
 * across Safari 14+ / Chrome 32+ / Firefox 65+ — no `<video>` autoplay
 * quirks, no MP4 black-box-behind-transparent issue.
 *
 * Honours `prefers-reduced-motion: reduce` by swapping in the static
 * first-frame WebP (~4KB), so users who opt out of motion still see the
 * token but don't get the spin.
 */
export function TokenSpin({ size = 40, className, title = 'EVI reward token' }: TokenSpinProps) {
  // Default to "no preference" so SSR / first paint renders the animation.
  // The effect flips us to static only when the user has the reduce pref.
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return (
    <img
      src={reducedMotion ? '/token-static.webp' : '/token-spin.webp'}
      alt={title}
      width={size}
      height={size}
      draggable={false}
      className={className}
      // Intrinsic dimensions + explicit size prevent CLS on first paint.
      style={{
        display: 'block',
        width: size,
        height: size,
        flexShrink: 0,
        // The asset is a circular coin with transparent surround — no
        // background, drop-shadow, or border applied per brand guidance.
      }}
    />
  );
}
